import { useQuery } from '@tanstack/react-query'
import type { ListMeta } from '@/lib/api'
import { useApi } from '@/lib/api-context'

/**
 * A trilha de ações da clínica ativa (docs/api/admin.md, "Trilha"; RN-ADM-60, RT-22). Só leitura:
 * a API não tem escrita, e a tabela não é expurgada. Os nomes são os de
 * `apps/api/supabase/functions/admin/audit-log/routes.ts` e de `admin_list_audit_log`.
 */

/** Quem agiu: pessoa logada, app de integração, job do sistema ou a paciente pelo link (RN-ANA-27). */
export type AuditActorType = 'user' | 'integration' | 'system' | 'patient'

/**
 * Os verbos canônicos conhecidos (docs/glossario.md, "Ações da trilha"). A API aceita qualquer
 * texto: áreas novas podem gravar um verbo antes de ele entrar aqui, e a tela precisa seguir legível.
 */
export const AUDIT_ACTIONS = [
  'created',
  'updated',
  'blocked',
  'unblocked',
  'archived',
  'restored',
  'deleted',
  'password_reset',
  'password_changed',
  'invite_resent',
  'role_changed',
  'clinic_switched',
  'mcp_connected',
  'mcp_disconnected',
  'send_requested',
  'resent',
  'canceled',
  'signed',
  'published',
  'activated',
  'deactivated',
  'generated',
  'regenerated',
  'discarded',
  'price_overridden',
  'downloaded',
  'undone',
  'read',
  'merged',
  'exported',
  'imported',
  'submitted',
  'closed',
  'revoked',
  'key_regenerated',
  'secret_revealed',
  'secret_rotated',
  'disabled',
  'linked',
  'unlinked',
  'draft_updated',
  'draft_discarded',
] as const

export type AuditAction = (typeof AUDIT_ACTIONS)[number]

/** O vocabulário fechado de `audit_log.target_type` (docs/banco/modelagem.md §4.1). */
export const AUDIT_TARGET_TYPES = [
  'membership',
  'user',
  'clinic',
  'network',
  'integration',
  'practitioner',
  'procedure',
  'patient',
  'questionnaire_version',
  'clinic_question_version',
  'question',
  'anamnesis',
  'template',
  'template_version',
  'template_activation',
  'appointment',
  'document',
  'signature_request',
  'signature',
  'treatment_plan',
  'treatment_plan_product',
  'photo',
  'photo_session',
  'region',
  'manufacturer',
  'catalog_product',
  'catalog_procedure',
  'catalog_procedure_request',
  'webhook_event',
  'webhook_endpoint',
  'dashboard',
] as const

export type AuditTargetType = (typeof AUDIT_TARGET_TYPES)[number]

export interface AuditActor {
  type: AuditActorType
  /** Nulo quando a ação foi do próprio sistema (carga inicial, job). */
  id: string | null
  /** Nome da pessoa, do app, "Westhetics" para o sistema ou "Paciente, pelo link". */
  name: string
}

export interface AuditTarget {
  type: AuditTargetType
  id: string | null
  /** Montado na leitura por tipo (nome da pessoa, da clínica…); nulo onde ainda não há regra. */
  label: string | null
}

export interface AuditEntry {
  id: string
  occurredAt: string
  actor: AuditActor
  action: AuditAction | (string & {})
  target: AuditTarget
  /** Nunca traz dado pessoal (RT-23). */
  summary: string | null
  /** Obrigatória nas ações que mudam prova ou acesso (RT-22). */
  justification: string | null
  requestId: string | null
}

export type AuditSort = '-occurredAt' | 'occurredAt'
export type AuditPerPage = 10 | 25 | 50

/** A query de `GET /admin/audit-log`. Datas em `aaaa-mm-dd`, no fuso da clínica (RT-21). */
export interface AuditLogParams {
  from?: string
  to?: string
  action?: string
  targetType?: AuditTargetType
  targetId?: string
  actorId?: string
  search?: string
  sort?: AuditSort
  page?: number
  perPage?: AuditPerPage
}

export const auditLogKeys = {
  all: ['audit-log'] as const,
  list: (params: object) => ['audit-log', 'list', params] as const,
}

export function useAuditLog(params: AuditLogParams) {
  const api = useApi()
  return useQuery({
    queryKey: auditLogKeys.list(params),
    queryFn: () =>
      api.list<AuditEntry, ListMeta>('/admin/audit-log', {
        from: params.from,
        to: params.to,
        action: params.action,
        targetType: params.targetType,
        targetId: params.targetId,
        actorId: params.actorId,
        search: params.search,
        sort: params.sort ?? '-occurredAt',
        page: params.page ?? 1,
        perPage: params.perPage ?? 25,
      }),
    // A lista anterior segura a tela enquanto a nova chega: trocar filtro não pisca esqueleto.
    placeholderData: (previous) => previous,
  })
}
